import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import { Google as GoogleIcon, Close as CloseIcon } from '@mui/icons-material';
import { auth, analytics } from '../firebase';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { styled } from '@mui/material/styles';
import { logEvent } from 'firebase/analytics';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    minWidth: '320px',
    backgroundColor: theme.palette.background.paper,
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  textTransform: 'none',
  padding: theme.spacing(1.5),
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[700]
        : theme.palette.grey[100],
  },
}));

export default function Auth({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create Google provider with Docs API scopes
  const createGoogleProvider = () => {
    const provider = new GoogleAuthProvider();
    // Add required Google Docs API scopes
    provider.addScope('https://www.googleapis.com/auth/documents');
    provider.addScope('https://www.googleapis.com/auth/drive.file');
    provider.addScope('https://www.googleapis.com/auth/drive');
    return provider;
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      logEvent(analytics, 'clicked_google_login', {});
      setError(null);
      const provider = createGoogleProvider();
      const result = await signInWithPopup(auth, provider);

      // Force token refresh to get latest access token
      await auth.currentUser.getIdToken(true);

      // Get Google access token for API calls
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;

      // Verify token before passing it
      if (!accessToken) throw new Error('Failed to retrieve access token');

      // Pass access token to parent component through onClose
      onClose(accessToken);
    } catch (err) {
      setError(err.message);
      logEvent(analytics, 'failed_google_login', { error: err.message });
      if (err.code === 'auth/network-request-failed') {
        setError('Network error - check your internet connection');
      }

      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign in popup was closed before completing');
      }
      // Handle scope denial errors
      if (err.code === 'auth/unauthorized-domain') {
        setError('Unauthorized domain - check Firebase configuration');
      }
      onClose(null); // Ensure dialog closes even on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={() => onClose(null)}>
      <IconButton
        aria-label="close"
        onClick={() => onClose(null)}
        sx={{
          position: 'absolute',
          right: theme => theme.spacing(2),
          top: theme => theme.spacing(2),
          color: theme => theme.palette.text.secondary,
          zIndex: 1, // Ensure the button is above other content
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle variant="h5" textAlign="center" sx={{ position: 'relative' }}>
        Google Docs Integration
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" textAlign="center" gutterBottom>
          This action requires access to your Google Docs. Please authenticate
          with the required permissions.
        </Typography>

        {error && (
          <Typography color="error" variant="body2" textAlign="center" mt={2}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <Divider sx={{ my: 2 }} />

      <DialogActions sx={{ flexDirection: 'column', gap: 2 }}>
        <GoogleButton
          fullWidth
          onClick={handleGoogleLogin}
          disabled={loading}
          startIcon={!loading && <GoogleIcon />}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Requesting Access...
            </>
          ) : (
            'Authorize Google Docs Access'
          )}
        </GoogleButton>

        <Typography
          variant="caption"
          textAlign="center"
          color="textSecondary"
          sx={{ px: 2 }}
        >
          Requires permission to create and modify documents in your Google Drive.
          We will not access existing documents without your permission.
        </Typography>
      </DialogActions>
    </StyledDialog>
  );
}